import { ANONYMOUS_PASSWORD, ANONYMOUS_USER, BASE_URL } from '@avanta/shared/constants/common';
import { t } from '@avanta/shared/i18n';
import { service } from '@avanta/shared/services/api';
import { oauthToken } from './index';

const RECOVER_PASS_URL = `${BASE_URL}users/password/recover`;
const VERIFY_PASS_URL = `${BASE_URL}users/password/code`;
const RESET_PASS_URL = `${BASE_URL}users/password/reset`;

export interface VerifyCodeParams extends Record<string, unknown> {
  validation_code: string;
  email: string;
}

export interface ChangePasswordParams extends Record<string, unknown> {
  email: string;
  password: string;
  validation_code: string;
}

const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const anonymousToken = await oauthToken(ANONYMOUS_USER, ANONYMOUS_PASSWORD);
  return {
    Authorization: `Bearer ${anonymousToken?.data.access_token}`,
    'Content-Type': 'application/json',
  };
};

const makeRequest = async (
  url: string,
  data: Record<string, unknown>,
  errorMessage: string
): Promise<void> => {
  const headers = await getAuthHeaders();
  try {
    await service.post(url, data, { headers });
  } catch {
    throw new Error(t(errorMessage));
  }
};

const makeTypedRequest = async <T extends Record<string, unknown>>(
  url: string,
  data: T,
  errorMessage: string
): Promise<void> => {
  const headers = await getAuthHeaders();
  try {
    await service.post(url, data, { headers });
  } catch {
    throw new Error(t(errorMessage));
  }
};

export const recoverPassword = async (email: string): Promise<void> => {
  await makeRequest(RECOVER_PASS_URL, { email }, 'RecoverPassword.error');
};

export const verifyRecoveryCode = async (params: VerifyCodeParams): Promise<void> => {
  await makeTypedRequest(VERIFY_PASS_URL, params, 'RecoverPassword.verificationError');
};

export const resendRecoveryCode = async (email: string): Promise<void> => {
  await makeRequest(RECOVER_PASS_URL, { email }, 'RecoverPassword.resendCodeError');
};

export const changePassword = async (params: ChangePasswordParams): Promise<void> => {
  await makeTypedRequest(RESET_PASS_URL, params, 'RecoverPassword.changePasswordError');
};

export const PasswordRecoveryService = {
  recoverPassword,
  verifyRecoveryCode,
  resendRecoveryCode,
  changePassword,
};
